import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'; // eslint-disable-line
import { Link } from 'react-router-dom';
import { FixedSizeList } from "react-window";
import uuid from 'react-uuid';
import PropTypes from 'prop-types';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Button, Checkbox, Divider, IconButton, Tooltip, LinearProgress, ListItem, ListItemText, Tab, Tabs, Typography, CircularProgress, InputBase } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { Stop, Delete, SaveAlt, Sync, Add, Edit, ExpandMore, FilterAlt, Sort, Search, OpenInNew, CreateNewFolder, CheckBoxOutlineBlank, IndeterminateCheckBox, CheckBox, VisibilityOff, Visibility, Public, PublicOff, UploadFile, Share, Highlight, HighlightOff, ContentCopy } from '@mui/icons-material';

import { 
  deleteWell, 
  makeWellCurrent, 
  selectWells, 
  toggleCheckWell,
  toggleCheckAllWells,
  toggleUncheckAllWells,
  stopProcessing,
  updateWellStatus,
  addWells,
  updateWellsStatus,
} from '../reducers/wellSlice';
import { selectProjects } from '../reducers/projectSlice';
import { addList, makeListCurrent, makeTypeCurveCurrent, makeGroupWellCurrent, selectLists, setForecastListTab } from '../reducers/listSlice';
import { getTypeCurveForecast, getTypeCurves, selectTypeCurves, toggleCheckAllTypeCurves, toggleCheckTypeCurve, toggleUncheckAllTypeCurves } from '../reducers/typeCurveSlice';
import { selectUser } from '../reducers/userSlice';
import { selectOptions, setShowWellListFilter } from '../reducers/optionsSlice';

import { NewWell } from './NewWell';
import { ExportData } from './ExportData';
import { colors } from '../helpers/colors';
import { formatUwi } from '../helpers/formatUwi';
import { EditTypeCurve } from './EditTypeCurve';
import { EditGroupWell } from './EditGroupWell';
import { getGroupWellForecast, getGroupWells, getWaterfloodForecastGW, selectGroupWells, toggleCheckAllGroupWells, toggleCheckGroupWell, toggleUncheckAllGroupWells } from '../reducers/groupWellSlice';
import { EditList } from './EditList';
import { filterWells } from '../helpers/filterWells';
import { ShareTypeCurve } from './ShareTypeCurve';
import { useSnackbar } from 'notistack';
import { ImportTypeCurve } from './ImportTypeCurve';

import ProcessSplitButton from './ProcessSplitButton';
import { useSocket } from '../context/useSocket';
import { chartColors } from '../helpers/chartColors';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%',
    position: 'relative',
    // backgroundColor: 'purple'
    // background: theme.palette.background2
  },
  well: {
    borderBottom: '1px solid lightgrey',
    margin: 0
  },
  wellOdd: {
    backgroundColor: 'lightgrey'
  },
  wellCurrent: {
    // backgroundColor: 'lightgrey',
    borderBottom: '1px solid lightgrey',
    margin: 0
  },
  wellContainer: {
    margin: 0,
    padding: 0,
    borderTop: '1px solid lightgrey'
  },
  listButtons: {
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 30,
    marginTop: 20,
    marginBottom: 20,
    padding: '0 10px',
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
  },
  subheading: {
    fontSize: theme.typography.pxToRem(12),
    marginLeft: 5
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  wellUwi: {
    fontSize: theme.typography.pxToRem(8),
  },
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20,
  },
  details: {
    alignItems: 'center',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  listsButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10,
    padding: '0 10px',
    width: '100%',
    backgroundColor: 'red'
  },
  listToolbar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: 0,
    padding: '0 4px'
  },
  wellButton: {
    margin: '0 10px'
  },
  listInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    marginLeft: 20,
    paddingRight: 20,
  },
  List: {
    zIndex: 69,
    // backgroundColor: 'purple'
    // backgroundColor: 'white'
  },
  tabs: {
    width: '100%'
  },
  tab: {
    width: 'auto',
    borderBottom: '1px solid lightgrey'
  },
  wellTab: {
    width: '100%',
    height: '100%',
    position: 'relative'
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginRight: 10,
    marginLeft: 10,
    paddingLeft: 5,
    paddingRight: 5,
    // border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit',
    width: '100%'
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24,
  },
}));

export const ForecastList = ({ projectId, listId, expandAll }) => {

  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { socket } = useSocket();

  const [newWellVisible, setNewWellVisible] = useState(false);
  const [searchUwi, setSearchUwi] = useState("");
  const [searchTypeCurve, setSearchTypeCurve] = useState("");
  const [searchGroupWell, setSearchGroupWell] = useState("");
  const [editListVisible, setEditListVisible] = useState(false);
  const [newTypeCurveVisible, setNewTypeCurveVisible] = useState(false);
  const [editTypeCurveVisible, setEditTypeCurveVisible] = useState(false);
  const [newGroupWellVisible, setNewGroupWellVisible] = useState(false);
  const [editGroupWellVisible, setEditGroupWellVisible] = useState(false);
  const [exportDataVisible, setExportDataVisible] = useState(false);
  const [sortDesc, setSortDesc] = useState(false);
  const [sortTypeCurvesDesc, setSortTypeCurvesDesc] = useState(false);
  const [sortGroupWellsDesc, setSortGroupWellsDesc] = useState(false);
  const [showActiveWellsOnly, setShowActiveWellsOnly] = useState(false);
  const [isolateActiveWell, setIsolateActiveWell] = useState(false);
  const [showActiveTypeCurvesOnly, setShowActiveTypeCurvesOnly] = useState(false);
  const [showPublicTypeCurves, setShowPublicTypeCurves] = useState(true);
  const [showActiveGroupWellsOnly, setShowActiveGroupWellsOnly] = useState(false);
  const [showPublicGroupWells, setShowPublicGroupWells] = useState(true);
  const [shareTypeCurveVisible, setShareTypeCurveVisible] = useState(false);
  const [importTypeCurveVisible, setImportTypeCurveVisible] = useState(false);

  const [expanded, setExpanded] = useState(expandAll);
  const [isTypeCurveLoading, setIsTypeCurveLoading] = useState(false);
  const [isGroupWellLoading, setIsGroupWellLoading] = useState(false);

  useEffect(() => setExpanded(expandAll), [expandAll]);

  const [isLoadingData, setIsLoadingData] = useState(false);

  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const currentProjectName = currentProject?.name || "";
  const lists = useSelector(selectLists);
  const list = lists.byId[listId];
  const filters = list?.filters;
  const allWells = useSelector(selectWells);  
  let allUwis = list?.wellUwis?.length > 0 ? (sortDesc ? list?.wellUwis?.filter(uwi => uwi?.length > 0) : Array.from(list?.wellUwis?.filter(uwi => uwi?.length > 0))?.reverse()) : [];
  if (showActiveWellsOnly) {
    allUwis = allUwis.filter(uwi => allWells?.byUwi?.[uwi]?.checked);
  };
  if (searchUwi.length > 0) allUwis = allUwis.filter(uwi => uwi.toLowerCase()?.includes(searchUwi?.toLowerCase()?.replace(/\W/ig, '')));

  const currentUwi = allWells.currentWellUwi;

  // type curves
  const typeCurves = useSelector(selectTypeCurves);
  let allTypeCurveIds = [...typeCurves?.allIds];
  sortTypeCurvesDesc ? allTypeCurveIds?.sort((a,b) => new Date(typeCurves.byId[a]?.lastModified) - new Date(typeCurves.byId[b]?.lastModified)) : allTypeCurveIds?.sort((a,b) => new Date(typeCurves.byId[b]?.lastModified) - new Date(typeCurves.byId[a]?.lastModified));
  if (showActiveTypeCurvesOnly) {
    allTypeCurveIds = allTypeCurveIds.filter(id => typeCurves?.byId?.[id]?.checked);
  };
  if (searchTypeCurve.length > 0) allTypeCurveIds = allTypeCurveIds.filter(id => typeCurves?.byId[id]?.name?.toLowerCase()?.includes(searchTypeCurve.toLowerCase()));
  if (!showPublicTypeCurves) allTypeCurveIds = allTypeCurveIds.filter(id => !typeCurves?.byId?.[id]?.public);
  const typeCurve = typeCurves.byId[list?.typeCurveId] || {};

  // group wells
  const groupWells = useSelector(selectGroupWells);
  let allGroupWellIds = [...groupWells?.allIds];
  sortGroupWellsDesc ? allGroupWellIds?.sort((a,b) => new Date(groupWells.byId[a]?.lastModified) - new Date(groupWells.byId[b]?.lastModified)) : allGroupWellIds?.sort((a,b) => new Date(groupWells.byId[b]?.lastModified) - new Date(groupWells.byId[a]?.lastModified));
  if (showActiveGroupWellsOnly) {
    allGroupWellIds = allGroupWellIds.filter(id => groupWells?.byId?.[id]?.checked);
  };
  if (searchGroupWell.length > 0) allGroupWellIds = allGroupWellIds.filter(id => groupWells?.byId[id]?.name?.toLowerCase()?.includes(searchGroupWell.toLowerCase()));
  if (!showPublicGroupWells) allGroupWellIds = allGroupWellIds.filter(id => !groupWells?.byId?.[id]?.public);
  const groupWell = groupWells.byId[list?.groupWellId] || {};

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';

  let wells = useMemo(() => {
    let filteredWells = filterWells(allUwis, allWells, filters);
    if (showActiveWellsOnly) {
      filteredWells.allUwis = filteredWells.allUwis.filter(uwi => allWells?.byUwi?.[uwi]?.checked);
    };
    return filteredWells;
  }, [allWells, filters, sortDesc, searchUwi, lists, showActiveWellsOnly]); //eslint-disable-line

  const processingWells = list?.wellUwis?.filter(uwi => wells.byUwi[uwi]?.status === 'processing')?.length > 0;

  function handleSort(e) {
    e.stopPropagation();
    setSortDesc(!sortDesc);
  };

  function handleTypeCurveSort(e) {
    e.stopPropagation();
    setSortTypeCurvesDesc(!sortTypeCurvesDesc);
  };

  function handleGroupWellSort(e) {
    e.stopPropagation();
    setSortGroupWellsDesc(!sortGroupWellsDesc);
  };

  function renderRow({index, style}) {

    const wellUwi = wells.allUwis?.[index];
    const formattedUwi = wells?.byUwi?.[wellUwi]?.formattedUwi || wellUwi;
    const currentWell = wellUwi === wells?.currentWellUwi;

    if (wells?.byUwi?.[wellUwi]) {
      return (
        <ListItem 
          button 
          style={{
            ...style, 
            opacity: (wells.byUwi[wellUwi].status === 'noData' || wells.byUwi[wellUwi].status === 'idle') && 0.5, 
            padding: '0 10px',
            backgroundColor: currentWell && (user.value.darkMode ? '#424242' : 'lightgrey'),
            display: 'flex',
            alignItems: 'center',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            // position: 'relative'
            // display: searchUwi.length > 0 && !wellUwi.includes(searchUwi) && 'none'
          }}
          key={index} 
          className={currentWell ? classes.wellCurrent : classes.well} 
          onClick={(e) => makeWellCurrentClick(e, list.id, wellUwi)}
          disablePadding
        >

          <div style={{
            position: 'absolute', 
            left: 0, 
            top: 0, 
            height: 50, 
            width: 5, 
            backgroundColor: wells.byUwi[wellUwi]?.status === 'processed' ? (wells.byUwi[wellUwi]?.color || chartColors?.[0]) : 'lightgrey'
          }}></div>

          <Checkbox checked={wells.byUwi[wellUwi]?.checked} onClick={e => checkWellClick(e, wellUwi)} style={{marginLeft: -10, marginRight: -5, color: (darkMode ? "#fff" : "primary")}} />
          <ListItemText
            primary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textPrimary"
                  style={{fontSize: 13, textOverflow: 'ellipsis', fontWeight: currentWell && 'bold'}}
                  noWrap
                >
                  {formattedUwi}
                </Typography>
              </>
            }
            secondary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textSecondary"
                  style={{fontSize: 10, textOverflow: 'ellipsis'}}
                >
                  {getWellStatus(wells.byUwi[wellUwi])}
                </Typography>
              </>
            }
            style={{textOverflow: 'ellipsis', width: 150, whiteSpace: 'nowrap'}}
          />
  
          <div style={{display: 'flex', marginTop: 0, alignItems: 'center'}}>
            <Tooltip title="Delete Well">
              <IconButton edge="end" size="small" style={{width: 24, height: 24, marginRight: 5}} color={darkMode ? "inherit" : "primary"} onClick={(e) => handleDeleteWellClick(e, listId, wellUwi)}>
                <Delete style={{width: 16, height: 16}} />
              </IconButton>
            </Tooltip>
          
            <Tooltip title={wells.byUwi[wellUwi]?.status === 'processing' ? 'Stop Processing' : 'Process Well (shortcut F10)'}>
              <IconButton 
                edge="end" 
                size="small" 
                color={darkMode ? 'inherit' : 'primary'}
                onClick={(e) => handleProcessWellClick(e, wellUwi)} 
                style={{width: 24, height: 24, marginRight: 0}}
                // disabled={wells.byUwi[wellUwi]?.local}
              >
                {wells.byUwi[wellUwi]?.status === 'processing' ? <Stop style={{width: 24, height: 24}} /> : <Sync style={{width: 24, height: 24}} />}
              </IconButton>
            </Tooltip>
            
          </div>

          {wells.byUwi[wellUwi]?.status === 'processing' && <LinearProgress style={{position: 'absolute', bottom: 0, left: 0, right: 0}} />}
          
        </ListItem>
      );
    } else {
      return (<></>);
    };
  };

  function renderTypeCurveRow({index, style}) {

    const typeCurveId = allTypeCurveIds?.[index];
    const name = typeCurves?.byId?.[typeCurveId]?.name || "Type Curve";
    const isTypeCurveCurrent = typeCurveId === list?.typeCurveId;

    if (typeCurves?.byId?.[typeCurveId]) {
      return (
        <ListItem 
          button 
          style={{
            ...style, 
            opacity: (!typeCurves?.byId?.[typeCurveId]?.production && !typeCurves?.byId?.[typeCurveId]?.forecast) && 0.5, 
            backgroundColor: isTypeCurveCurrent && (user.value.darkMode ? '#424242' : 'lightgrey'),
            padding: '0 10px',
            display: 'flex',
            alignItems: 'center',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }}
          key={index} 
          className={isTypeCurveCurrent ? classes.wellCurrent : classes.well} 
          onClick={() => {handleTypeCurveClick(list?.id, typeCurveId)}}
          disablePadding
        >
          <div style={{
            position: 'absolute', 
            left: 0, 
            top: 0, 
            height: 50, 
            width: 5, 
            backgroundColor: typeCurves?.byId?.[typeCurveId]?.color || chartColors?.[0]
          }}></div>
          <Checkbox checked={typeCurves?.byId?.[typeCurveId]?.checked} onClick={e => checkTypeCurveClick(e, typeCurveId)} color="primary" style={{marginLeft: -10}} />
          <ListItemText
            primary={
              <div style={{width: 160, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textPrimary"
                  style={{fontSize: 13}}
                  noWrap
                >
                  {name}
                </Typography>
              </div>
            }
            secondary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textSecondary"
                  style={{fontSize: 10, textOverflow: 'ellipsis'}}
                  noWrap
                >
                  {`Wells: ${typeCurves?.byId?.[typeCurveId]?.uwis?.length || 0} |  
                  ${typeCurves?.byId?.[typeCurveId]?.fullCycle ? 'Prod+Forecast' : 'Production Only'} |
                  ${typeCurves?.byId?.[typeCurveId]?.public ? 'Public' : 'Private'}`}
                </Typography>
              </>
            }
          />
  
          {typeCurveId === isTypeCurveLoading ? 
            <CircularProgress style={{width: 24, height: 24, position: 'absolute', right: 20}} />
            :
            <div style={{display: 'flex', marginTop: 0}}>
              <Tooltip title="Edit Type Curve">
                <span><IconButton 
                  edge="end" 
                  size="small" 
                  color={darkMode ? "inherit" : "primary"} 
                  style={{width: 24, height: 24, marginRight: 0}} 
                  onClick={(e) => {handleEditTypeCurve(e, listId, typeCurveId)}}
                  disabled={!typeCurves.byId?.[typeCurveId]?.checked || typeCurves.byId?.[typeCurveId]?.createdBy !== user?.value?.email}
                >
                  <Edit style={{width: 16, height: 16}} />
                </IconButton></span>
              </Tooltip>
              <Tooltip title="Duplicate Type Curve">
                <span><IconButton 
                  edge="end" 
                  size="small" 
                  color={darkMode ? "inherit" : "primary"} 
                  style={{width: 24, height: 24, marginRight: 0}} 
                  onClick={(e) => {handleDuplicateTypeCurve(typeCurves.byId?.[typeCurveId])}}
                  disabled={!typeCurves.byId?.[typeCurveId]?.checked}
                >
                  <ContentCopy style={{width: 16, height: 16}} />
                </IconButton></span>
              </Tooltip>
              <Tooltip title="Open Wells in New List">
                <span><IconButton 
                  edge="end" 
                  size="small" 
                  color={darkMode ? "inherit" : "primary"} 
                  style={{width: 24, height: 24}} 
                  onClick={e => handleDownloadWells(e, typeCurves.byId?.[typeCurveId]?.name, typeCurveId, "", typeCurves.byId?.[typeCurveId]?.uwis)}
                  disabled={!typeCurves.byId?.[typeCurveId]?.checked}
                >
                  <CreateNewFolder style={{width: 16, height: 16}} />
                </IconButton></span>
              </Tooltip>
            </div>
          }

        </ListItem>
      );
    } else {
      return (<></>);
    };
  };

  function renderGroupWellRow({index, style}) {

    const groupWellId = allGroupWellIds?.[index];
    const name = groupWells?.byId?.[groupWellId]?.name || "Group Well";
    const isGroupWellCurrent = groupWellId === list?.groupWellId;

    if (groupWells?.byId?.[groupWellId]) {
      return (
        <ListItem 
          button 
          style={{
            ...style, 
            opacity: (!groupWells?.byId?.[groupWellId]?.forecast || Object.keys(groupWells?.byId?.[groupWellId]?.forecast)?.length === 0) && 0.5, 
            backgroundColor: isGroupWellCurrent && (user.value.darkMode ? '#424242' : 'lightgrey'),
            padding: '0 10px',
            display: 'flex',
            alignItems: 'center',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }}
          key={index} 
          className={isGroupWellCurrent ? classes.wellCurrent : classes.well} 
          onClick={() => {handleGroupWellClick(list.id, groupWellId)}}
          disablePadding
        >
          <div style={{
            position: 'absolute', 
            left: 0, 
            top: 0, 
            height: 50, 
            width: 5, 
            backgroundColor: groupWells?.byId?.[groupWellId]?.color || chartColors?.[0]
          }}></div>
          <Checkbox checked={groupWells?.byId?.[groupWellId]?.checked} onClick={e => checkGroupWellClick(e, groupWellId)} color="primary" style={{marginLeft: -10}} />
          <ListItemText
            primary={
              <div style={{width: 160, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textPrimary"
                  style={{fontSize: 13}}
                  noWrap
                >
                  {name}
                </Typography>
              </div>
            }
            secondary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.inline}
                  color="textSecondary"
                  style={{fontSize: 10, textOverflow: 'ellipsis'}}
                  noWrap
                >
                  {`Wells: ${groupWells?.byId?.[groupWellId]?.uwis?.length || 0} | 
                  ${groupWells?.byId?.[groupWellId]?.fullCycle ? 'Prod+Forecast' : 'Production Only'} |
                  ${groupWells?.byId?.[groupWellId]?.public ? 'Public' : 'Private'}`}
                </Typography>
              </>
            }
          />
  
          {groupWellId === isGroupWellLoading ? 
            <CircularProgress style={{width: 24, height: 24, position: 'absolute', right: 20}} />
            :
          <div style={{display: 'flex', marginTop: 0}}>
            <Tooltip title="Edit Group Well">
              <span><IconButton 
                edge="end" 
                size="small" 
                color={darkMode ? "inherit" : "primary"} 
                style={{width: 24, height: 24, marginRight: 0}} 
                onClick={(e) => {handleEditGroupWell(e, listId, groupWellId)}}
                disabled={!groupWells.byId?.[groupWellId]?.checked || groupWells.byId?.[groupWellId]?.createdBy !== user?.value?.email}
              >
                <Edit style={{width: 16, height: 16}} />
              </IconButton></span>
            </Tooltip>
            <Tooltip title="Duplicate Group Well">
              <span><IconButton 
                edge="end" 
                size="small" 
                color={darkMode ? "inherit" : "primary"} 
                style={{width: 24, height: 24, marginRight: 0}} 
                onClick={(e) => {handleDuplicateGroupWell(groupWells.byId?.[groupWellId])}}
                disabled={!groupWells.byId?.[groupWellId]?.checked}
              >
                <ContentCopy style={{width: 16, height: 16}} />
              </IconButton></span>
            </Tooltip>
            <Tooltip title="Open Wells in New List">
              <span><IconButton 
                edge="end" 
                size="small" 
                color={darkMode ? "inherit" : "primary"} 
                style={{width: 24, height: 24, marginRight: 0}} 
                onClick={e => handleDownloadWells(e, groupWells.byId?.[groupWellId]?.name, groupWellId, "", groupWells.byId?.[groupWellId]?.uwis)}
                disabled={!groupWells.byId?.[groupWellId]?.checked}
              >
                <CreateNewFolder style={{width: 16, height: 16, marginRight: 0}} />
              </IconButton></span>
            </Tooltip>
          </div>}
        </ListItem>
      );
    } else {
      return (<></>);
    };
  };

  renderRow.propTypes = {
    index: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  renderTypeCurveRow.propTypes = {
    index: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  renderGroupWellRow.propTypes = {
    index: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
  };

  const wellTab = (
    <div className={classes.wellTab}>
      <div className={classes.listButtons}>
        <Tooltip title="Add Wells">
          <span><Button 
            variant="outlined" 
            size='small' 
            style={{height: 28}}
            color={darkMode ? "inherit" : "primary"} 
            startIcon={<Add />} 
            onClick={e => handleNewWellClick(e)}
            disabled={demoUser}
          >
            Wells
          </Button></span>
        </Tooltip>

        <ProcessSplitButton running={processingWells} darkMode={darkMode} handleProcessClick={handleProcessClick} demoUser={demoUser} />

      </div>

      <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
        <div className={classes.searchIcon}>
          <Search color={darkMode ? "inherit" : "primary"} />
        </div>
        <InputBase
          placeholder="Search wells by UWI"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={searchUwi}
          onChange={handleUwiSearch}
        />
      </div>

      <div style={{marginLeft: 10, marginTop: 5, marginBottom: 5}}>
        <Typography variant="subtitle2" color="textPrimary" style={{fontSize: 12}}>
          {`Wells: ${wells.allUwis?.length} | Sorted by: ${sortDesc ? 'oldest' : 'newest'} | Filters: ${filters ? Object.keys(filters)?.length : 0}`}
        </Typography>
      </div>
      <div className={classes.listToolbar}>
        <div>
          <Tooltip title="Select Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleCheckAllWells(listId)} style={{marginRight: 0}}>
              {getCheckedWellsIcon(listId)}
            </IconButton>
          </Tooltip>
        </div>
        <div>
          <Tooltip title="Bulk Export Data">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleExportDataClick()} style={{marginRight: 5}}>
              <SaveAlt fontSize="24px" />
            </IconButton>  
          </Tooltip>
          <Tooltip title="Show Selected Only">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => setShowActiveWellsOnly(prev => !prev)} style={{marginRight: 5}}>
              {!showActiveWellsOnly ? <Visibility fontSize="24px" /> : <VisibilityOff fontSize="24px" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isolateActiveWell ? 'Exit Isolation Mode' : 'Isolate Current Well'}>
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => handleIsolateActiveWell(isolateActiveWell, currentUwi)} style={{marginRight: 5}}>
              {!isolateActiveWell ? <Highlight fontSize="24px" /> : <HighlightOff fontSize="24px" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Sort Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleSort(e)} style={{marginRight: 5}}>
              <Sort fontSize="24px" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={(e) => handleFilterClick(e, listId)} style={{marginRight: 5}}>
              <FilterAlt fontSize="24px" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      
      <FixedSizeList 
        height={400} 
        width={'100%'}
        minHeight={60} 
        itemSize={50} 
        itemCount={allUwis?.length}
        className={classes.wellContainer}
      >
        {renderRow}
      </FixedSizeList>
    </div>
  );

  const typeCurveTab = (
    <div className={classes.wellTab}>
      <div className={classes.listButtons}>
        <div>
          <Tooltip title="New Type Curve">
            <Button size="small" startIcon={<Add />} variant="contained" color={darkMode ? "inherit" : "primary"}
              style={{color: darkMode ? '#000' : '#fff', marginRight: 10}} onClick={newTypeCurveClick}>
              New
            </Button>
          </Tooltip>
          <Tooltip title="Import Type Curve">
            <Button size="small" startIcon={<UploadFile />} variant="outlined" color={darkMode ? "inherit" : "primary"} onClick={() => setImportTypeCurveVisible(true)}>
              Import
            </Button>
          </Tooltip>
        </div>
        <Tooltip title="Update Type Curves">
          <IconButton 
            // startIcon={<Sync />} 
            size='small' 
            variant='contained' 
            color={darkMode ? "inherit" : "primary"}
            style={{color: darkMode ? '#000' : '#0758B7', width: 40, height: 40}}
            onClick={() => loadTypeCurves(user?.value?.email)}
          >
            <Sync />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.search} style={{border: `1px solid ${darkMode ? "#fff" : theme.palette.primary.main}`}}>
        <div className={classes.searchIcon}>
          <Search color={darkMode ? "inherit" : "primary"} />
        </div>
        <InputBase
          placeholder="Search type curves by name"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={searchTypeCurve}
          onChange={handleTypeCurveSearch}
        />
      </div>

      <div style={{marginLeft: 10, marginTop: 5, marginBottom: 5}}>
        <Typography variant="subtitle2" color="textPrimary" style={{fontSize: 12}}>
          {`Type curves: ${allTypeCurveIds?.length} | Sorted by: ${sortTypeCurvesDesc ? 'oldest' : 'newest'} | Filters: ${filters ? Object.keys(filters)?.length : 0}`}
        </Typography>
      </div>
      <div className={classes.listToolbar}>
        <div>
          <Tooltip title="Select Type Curves">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleCheckAllTypeCurves()} style={{marginRight: 0}}>
              {getCheckedTypeCurvesIcon()}
            </IconButton>
          </Tooltip>
        </div>
        <div>
          <Tooltip title="Bulk Export Data">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleExportDataClick()} style={{marginRight: 5}}>
              <SaveAlt fontSize="24px" />
            </IconButton>  
          </Tooltip>
          <Tooltip title="Share Type Curve">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => setShareTypeCurveVisible(true)} style={{marginRight: 5}}>
              <Share fontSize="24px" />
            </IconButton>  
          </Tooltip>
          <Tooltip title="Show Selected Only">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => setShowActiveTypeCurvesOnly(prev => !prev)} style={{marginRight: 5}}>
              {!showActiveTypeCurvesOnly ? <Visibility fontSize="24px" /> : <VisibilityOff fontSize="24px" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Show Public Type Curves">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => setShowPublicTypeCurves(prev => !prev)} style={{marginRight: 5}}>
              {showPublicTypeCurves ? <Public fontSize="24px" /> : <PublicOff fontSize="24px" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Sort Type Curves">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleTypeCurveSort(e)} style={{marginRight: 5}}>
              <Sort fontSize="24px" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={(e) => handleFilterClick(e, listId)} style={{marginRight: 5}}>
              <FilterAlt fontSize="24px" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {isLoadingData ? 
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: 400}}>
          <Typography style={{marginBottom: 20}}>Loading type curves...</Typography>
          <CircularProgress></CircularProgress>
        </div>
        :
          <FixedSizeList 
          height={400} 
          width={'100%'}
          minHeight={60} 
          itemSize={50} 
          itemCount={allTypeCurveIds?.length}
          className={classes.wellContainer}
        >
          {renderTypeCurveRow}
        </FixedSizeList>
      }
    </div>
  );

  const groupWellTab = (
    <div className={classes.wellTab}>
      <div className={classes.listButtons}>
        <Tooltip title="New Group Well">
          <Button size="small" startIcon={<Add />} variant="contained" color={darkMode ? "inherit" : "primary"}
            style={{color: darkMode ? '#000' : '#fff', marginRight: 10}} onClick={newGroupWellClick}>
            New
          </Button>
        </Tooltip>
        <Tooltip title="Update Group Wells">
          <IconButton 
            size='small' 
            variant='contained' 
            color={darkMode ? "inherit" : "primary"}
            style={{color: darkMode ? '#000' : '#0758B7', width: 40, height: 40}}
            onClick={() => loadGroupWells(user?.value?.email)}
          >
            <Sync />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.search} style={{border: `1px solid ${darkMode ? "#fff" : theme.palette.primary.main}`}}>
        <div className={classes.searchIcon}>
          <Search color={darkMode ? "inherit" : "primary"} />
        </div>
        <InputBase
          placeholder="Search group wells by name"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={searchGroupWell}
          onChange={handleGroupWellSearch}
        />
      </div>

      <div style={{marginLeft: 10, marginTop: 5, marginBottom: 5}}>
        <Typography variant="subtitle2" color="textPrimary" style={{fontSize: 12}}>
          {`Group wells: ${allGroupWellIds?.length} | Sorted by: ${sortGroupWellsDesc ? 'oldest' : 'newest'} | Filters: ${filters ? Object.keys(filters)?.length : 0}`}
        </Typography>
      </div>
      <div className={classes.listToolbar}>
        <div>
          <Tooltip title="Select Group Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleCheckAllGroupWells()} style={{marginRight: 0}}>
              {getCheckedGroupWellsIcon()}
            </IconButton>
          </Tooltip>
        </div>
        <div>
          <Tooltip title="Bulk Export Data">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleExportDataClick()} style={{marginRight: 5}}>
              <SaveAlt fontSize="24px" />
            </IconButton>  
          </Tooltip>
          <Tooltip title="Show Selected Only">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => setShowActiveGroupWellsOnly(prev => !prev)} style={{marginRight: 5}}>
              {!showActiveGroupWellsOnly ? <Visibility fontSize="24px" /> : <VisibilityOff fontSize="24px" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Show Public Group Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={() => setShowPublicGroupWells(prev => !prev)} style={{marginRight: 5}}>
              {showPublicGroupWells ? <Public fontSize="24px" /> : <PublicOff fontSize="24px" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Sort Group Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={e => handleGroupWellSort(e)} style={{marginRight: 5}}>
              <Sort fontSize="24px" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter Wells">
            <IconButton size="small" color={darkMode ? "inherit" : "primary"} onClick={(e) => handleFilterClick(e, listId)} style={{marginRight: 5}}>
              <FilterAlt fontSize="24px" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {isLoadingData ? 
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: 400}}>
          <Typography style={{marginBottom: 20}}>Loading group wells...</Typography>
          <CircularProgress></CircularProgress>
        </div>
        :
        <FixedSizeList 
          height={400} 
          width={'100%'}
          minHeight={60} 
          itemSize={50} 
          itemCount={allGroupWellIds?.length || 0}
          className={classes.wellContainer}
        >
          {renderGroupWellRow}
        </FixedSizeList>
      }
    </div>
  );

  if (!list) {
    return (<></>);
  };

  return (
    <div className={classes.root}>
      {newWellVisible && <NewWell newWellClose={newWellClose} listId={listId} handleProcessClick={() => handleProcessClick()} />}
      {newTypeCurveVisible && <EditTypeCurve 
        listId={listId} 
        color={colors[Math.floor(Math.random() * colors.length - 1)]} 
        typeCurveClose={newTypeCurveClose} 
        wells={wells} 
      />}
      {newGroupWellVisible && <EditGroupWell listId={listId} color={colors[Math.floor(Math.random() * colors.length - 1)]} groupWellClose={newGroupWellClose} wells={wells} />}
      {editTypeCurveVisible && <EditTypeCurve listId={listId} color={colors[Math.floor(Math.random() * colors.length - 1)]} typeCurveClose={editTypeCurveClose} wells={wells} typeCurve={typeCurve}/>}
      {editGroupWellVisible && <EditGroupWell listId={listId} color={colors[Math.floor(Math.random() * colors.length - 1)]} groupWellClose={editGroupWellClose} wells={wells} groupWell={groupWell} />}
      {editListVisible && <EditList projectId={projectId} listId={listId} editListClose={editListClose} />}
      {exportDataVisible && <ExportData projectName={currentProjectName} handleExportDataClose={handleExportDataClose} />}
      {shareTypeCurveVisible && <ShareTypeCurve 
        typeCurve={typeCurve} 
        darkMode={darkMode} 
        setShareTypeCurveVisible={setShareTypeCurveVisible} 
        handleExportTypeCurveJSONClick={handleExportTypeCurveJSONClick}
        handleCopyTypeCurveLink={handleCopyTypeCurveLink}
      />}
      
      {importTypeCurveVisible && <ImportTypeCurve importTypeCurve={importTypeCurve} setImportTypeCurveVisible={setImportTypeCurveVisible} darkMode={darkMode} userEmail={user?.value?.email} />}

      <Accordion 
        // defaultExpanded
        expanded={expanded}
        className={classes.List}
        style={{
          opacity: list?.id === lists?.currentListId ? 1 : 0.5,
        }}
        sx={{
          boxShadow: 1,
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        }}
        onClick={(e) => {
          makeListCurrentClick(e, list?.id);
        }}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />} onClick={() => setExpanded(prev => !prev)}>
          <div style={{width: 240, textOverflow: 'ellipsis'}}><Typography className={classes.heading} noWrap>{list?.name}</Typography></div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>

          <div className={classes.listInfo}>
            <Typography className={classes.subheading} color="textSecondary">{`Last Modified: ${new Date(list?.lastModified)?.toLocaleString()}`}</Typography>
            <Typography className={classes.subheading} color="textSecondary">{`Type Curve: ${typeCurve?.name || "Not Selected"}`}</Typography>
            <Typography className={classes.subheading} color="textSecondary">{`Group Well: ${groupWell?.name || "Not Selected"}`}</Typography>
          </div>

          <Tabs 
            value={list?.forecastListTab || 0}
            TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
            textColor={darkMode ? "inherit" : "primary"}
            onChange={handleTabChange}
            className={classes.tabs}
            style={{
              backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
              borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
            }}
          >  
            <Tab label="Wells" className={classes.tab} />
            <Tab label="Type Curves" className={classes.tab} />
            <Tab label="Group Wells" className={classes.tab} />
          </Tabs>
          
          {list?.forecastListTab === 0 ? wellTab : list?.forecastListTab === 1 ? typeCurveTab : groupWellTab}

        </AccordionDetails>
        <Divider />
        <AccordionActions style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Button size="small" startIcon={<Edit />} color={darkMode ? "inherit" : "primary"} type="text" onClick={e => editListClick(e)}>
            Edit
          </Button>
          <Button size="small" startIcon={<OpenInNew />} color={darkMode ? "inherit" : "primary"} type="text" to="/wells" component={Link}>
            View All
          </Button>
        </AccordionActions>
      </Accordion>
    </div>
  );

  
  // FUNCTIONS
  function handleUwiSearch(e) {
    setSearchUwi(e.target.value);
  };

  function handleTypeCurveSearch(e) {
    setSearchTypeCurve(e.target.value);
  };

  function handleGroupWellSearch(e) {
    setSearchGroupWell(e.target.value);
  };

  function handleExportDataClick() {
    setExportDataVisible(true);
  };

  function handleFilterClick(e, listId) {
    e.stopPropagation();
    !options?.showWellListFilter ? dispatch(setShowWellListFilter(listId)) : dispatch(setShowWellListFilter(false));
  };

  function loadTypeCurves(email) {
    console.log('Loading type curves...');
    setIsLoadingData(true);
    dispatch(getTypeCurves({ email, currentTypeCurveId: list?.typeCurveId })).then(tc => {
      let checkedTypeCurve = null;

      if (list?.typeCurveId) {
        checkedTypeCurve = tc?.payload?.typeCurves?.filter(t => t.id === list?.typeCurveId)?.[0];
      };
      if (!checkedTypeCurve) {
        checkedTypeCurve = tc?.payload?.typeCurves?.[tc?.payload?.typeCurves?.length - 1];
      };

      if (!checkedTypeCurve?.forecast) {
        dispatch(getTypeCurveForecast(checkedTypeCurve));
      };

      dispatch(toggleCheckTypeCurve({ typeCurveId: checkedTypeCurve?.id }));
      dispatch(makeTypeCurveCurrent({listId: listId, typeCurveId: checkedTypeCurve?.id}));
      setIsLoadingData(false);
    });
  };

  function loadGroupWells(email) {
    console.log('Loading group wells...');
    setIsLoadingData(true);
    dispatch(getGroupWells(email)).then(gw => {
      let checkedGroupWell = null;

      if (list?.groupWellId) {
        checkedGroupWell = gw?.payload?.filter(g => g.id === list?.groupWellId)?.[0];
      };
      if (!checkedGroupWell) {
        checkedGroupWell = gw?.payload?.[0];
      };

      if (!checkedGroupWell?.forecast) {
        dispatch(getGroupWellForecast(checkedGroupWell));
      };

      dispatch(toggleCheckGroupWell({ groupWellId: checkedGroupWell?.id }));
      dispatch(makeGroupWellCurrent({listId: listId, groupWellId: checkedGroupWell?.id}));
      setIsLoadingData(false);
    });
  };

  function handleEditTypeCurve(e, listId, typeCurveId) {
    e.stopPropagation();
    dispatch(makeTypeCurveCurrent({listId: listId, typeCurveId: typeCurveId}));
    setEditTypeCurveVisible(true);
  };

  function handleEditGroupWell(e, listId, groupWellId) {
    e.stopPropagation();
    dispatch(makeGroupWellCurrent({listId: listId, groupWellId: groupWellId}));
    setEditGroupWellVisible(true);
  };

  function handleDownloadWells(e, name, typeCurveId, groupWellId, uwis) {
    e.stopPropagation();
    e.preventDefault();
    const id = uuid();
    const color = colors[Math.floor(Math.random() * colors.length - 1)];
    let wells = {};

    const list = {
      id,
      name: name,
      description: `Created from type curve ${name}`,
      color,
      createdBy: user.value.email,
      lastModified: new Date().toLocaleString(),
      wellUwis: uwis,
      typeCurveId: typeCurveId,
      groupWellId: groupWellId,
      forecastListTab: 0,
      filters: {}
    };
    dispatch(addList({projectId: (projectId || "default"), list}));

    for (let uwi of uwis) {
      const color = colors[Math.floor(Math.random() * colors.length - 1)];
      wells[uwi] = {
        uwi,
        formattedUwi: formatUwi(uwi),
        color,
        lastModified: new Date().toLocaleString(),
        status: 'processing'
      };
    };

    dispatch(addWells({uwis: uwis, wells: wells, listId: id}));
    sendProcessRequest(uwis, false);
  };

  async function handleTypeCurveClick(listId, typeCurveId) {
    try {
      const typeCurve = typeCurves?.byId?.[typeCurveId];

      if (!typeCurve?.production && typeCurve?.location && typeCurve?.location?.length > 0) {
        setIsTypeCurveLoading(typeCurve?.id);
        await importTypeCurve(typeCurve?.location, user?.value?.email);
        setIsTypeCurveLoading(false);
      };


      if (!typeCurve?.forecast) { // generate forecast
        console.log('Calculating forecast...')
        dispatch(getTypeCurveForecast(typeCurve));
      };

      
      dispatch(makeTypeCurveCurrent({listId: listId, typeCurveId: typeCurveId}));
      if (!typeCurves?.byId?.[typeCurveId]?.checked) {
        dispatch(toggleCheckTypeCurve({ typeCurveId }));
      };
      // if (!typeCurve?.forecast) {
      //   dispatch(getTypeCurveForecast(typeCurve));
      // };
      

    } catch (error) {
      console.log(error);
    };
  };

  function handleDuplicateTypeCurve(tc) {
    try {
      dispatch(getTypeCurveForecast({...tc, id: uuid(), public: false, createdBy: user?.value?.email, lastModified: new Date()}));
    } catch (error) {
      console.log(error);
    };
  };

  async function importTypeCurve(location, userEmail) {
    try {
      console.log(`Fetching type curve from S3 ${location}...`);

      const res = await fetch(location, {cache: "no-store"});
      const tc = await res.json();

      if (!tc) return;

      dispatch(getTypeCurveForecast({...tc, location}));

      // if (tc?.createdBy !== userEmail && !tc?.public) {
      //   console.log('Changing type curve owner...');
      //   dispatch(getTypeCurveForecast({...tc, id: uuid(), createdBy: userEmail, lastModified: new Date()}));
      // } else {
      //   dispatch(getTypeCurveForecast({...tc, location}));
      // };

    } catch (error) {
      console.log(error);
      return;
    };
  };

  async function handleGroupWellClick(listId, groupWellId) {
    let groupWell = groupWells?.byId?.[groupWellId];
    if (!groupWell?.production && groupWell?.location && groupWell?.location?.length > 0) {
      setIsGroupWellLoading(groupWell?.id);
      groupWell = await importGroupWell(groupWell?.location, user?.value?.email);
      setIsGroupWellLoading(false);
    };

    // if (!groupWell?.forecast) { // generate forecast
    //   if (groupWell?.params?.oil?.useWaterflood) {
    //     console.log('Calculating group well waterflood forecast...');
    //     dispatch(getWaterfloodForecastGW(groupWell));
    //   } else {
    //     console.log('Calculating group well forecast...');
    //     dispatch(getGroupWellForecast(groupWell));
    //   };
    // };

    dispatch(makeGroupWellCurrent({listId, groupWellId}));
    if (!groupWell?.checked) {
      dispatch(toggleCheckGroupWell({ groupWellId }));
    };
  };

  function handleDuplicateGroupWell(gw) {
    try {
      dispatch(getGroupWellForecast({...gw, id: uuid(), public: false, createdBy: user?.value?.email, lastModified: new Date()}));
    } catch (error) {
      console.log(error);
    };
  };

  async function importGroupWell(location, userEmail) {
    try {
      console.log(`Fetching group well from S3 ${location}...`);

      const res = await fetch(location, {cache: "no-store"});
      const gw = await res.json();

      if (!gw) return;

      if (gw?.params?.oil?.useWaterflood) {
        console.log('Calculating waterflood forecast...');
        dispatch(getWaterfloodForecastGW(gw));
      } else {
        console.log('Calculating group well forecast...');
        dispatch(getGroupWellForecast(gw));
      };
      return gw;

      // if (gw?.createdBy !== userEmail && !gw?.public) {
      //   console.log('Changing group well owner...');
      //   if (gw?.params?.oil?.useWaterflood) {
      //     dispatch(getWaterfloodForecastGW({...gw, id: uuid(), createdBy: userEmail, lastModified: new Date()}));
      //   } else {
      //     dispatch(getGroupWellForecast({...gw, id: uuid(), createdBy: userEmail, lastModified: new Date()}));
      //   };
      //   return {...gw, id: uuid(), createdBy: userEmail, lastModified: new Date()};
      // } else {
      //   if (gw?.params?.oil?.useWaterflood) {
      //     console.log('Calculating waterflood forecast...');
      //     dispatch(getWaterfloodForecastGW(gw));
      //   } else {
      //     console.log('Calculating group well forecast...');
      //     dispatch(getGroupWellForecast(gw));
      //   };
      //   return gw;
      // };

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleExportTypeCurveJSONClick() {
    try {
      let dataStr = JSON.stringify({...typeCurve, id: uuid(), public: false, createdBy: user?.value?.email});
      if (!dataStr?.length > 0) return;

      let dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const date = new Date().toLocaleDateString();
      let exportFileDefaultName = `${typeCurve?.name}_${date}.json`;
      let linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      setShareTypeCurveVisible(false);
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleCopyTypeCurveLink() {
    try {
      let copyText = typeCurve?.location;

      if (!copyText) {
        enqueueSnackbar('Type curve link could not be copied', {
          variant: 'error'
        });
        setShareTypeCurveVisible(false);
        return;
      };
      navigator.clipboard.writeText(copyText);
    
      enqueueSnackbar('Type curve link copied to clipboard', {
        variant: 'info'
      });
      setShareTypeCurveVisible(false);

    } catch (error) {
      console.log(error);
    };
  };

  function handleExportDataClose() {
    setExportDataVisible(false);
  };

  function handleIsolateActiveWell(isolateActiveWell, uwi) {
    try {
      
      const backgroundUwis = allUwis.filter(u => u !== uwi);

      if (isolateActiveWell) {
        dispatch(toggleCheckAllWells({wellUwis: backgroundUwis}));
        setIsolateActiveWell(false);
      } else {
        dispatch(toggleUncheckAllWells({wellUwis: backgroundUwis}));
        setIsolateActiveWell(true);
      };

    } catch (error) {
      console.log(error);
    };
  };

  function sendProcessRequest(uwis, reFit) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });

      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params: {},
          localParams,
          reFit,
          assistedForecast: (options?.assistedForecast || false), 
          tcParams: typeCurve.params
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };

  async function handleProcessClick(reFit) {
    try {
      if (processingWells) {
        dispatch(stopProcessing());
        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({
            messageType: 'killall', userEmail: user?.value?.email
          }));
        };
      } else {
        let uwis = [];
        let localUwis = [];
  
        allUwis?.forEach(uwi => {
          const well = wells.byUwi[uwi];
          if (!well?.local && well?.checked) {
            uwis.push(uwi);
          };
        });
        if (uwis.length === 0) {
          allUwis?.forEach(uwi => {
            const well = wells.byUwi[uwi];
            if (!well?.local && well?.checked && well?.status === 'processed') {
              uwis.push(uwi);
            } else if (well?.local) {
              localUwis.push(uwi);
            };
          });
        };      
  
        if (uwis?.length > 0) {
          console.log('Dispatching ' + uwis.length + ' wells...');
          dispatch(updateWellsStatus({uwis, status: 'processing'})); // Set wells' status to processing
          sendProcessRequest(uwis, reFit);
        };
  
        if (localUwis?.length > 0) {
          console.log('Dispatching ' + localUwis.length + ' local wells...');
          let localWells = [];
          localUwis.forEach(uwi => {
            localWells.push(wells.byUwi[uwi]);
          });
          dispatch(updateWellsStatus({uwis: localUwis, status: 'processing'})); // Set wells' status to processing
          
          if (socket && socket.readyState === 1) {
            socket.send(JSON.stringify({
              messageType: 'localWell', 
              payload: localWells,
              userEmail: user?.value?.email,
              assistedForecast: options?.assistedForecast,
              tcParams: typeCurve?.params
            }));
          };
        };
      };
    } catch (error) {
      console.log(error);
    };
    
  };

  function handleProcessWellClick(e, wellUwi) {
    try {
      const well = wells?.byUwi?.[wellUwi];
      const status = well?.status;

      if (!well) return;

      if (!well?.local) {
        if (status === 'processing') {
          dispatch(updateWellStatus({uwi: wellUwi, status: 'idle'}));
        } else {
          dispatch(updateWellStatus({uwi: wellUwi, status: 'processing'}));
          sendProcessRequest([wellUwi], false);
        };
      } else { // if local well
        let wells = [well];
        dispatch(updateWellStatus({uwi: wellUwi, status: 'processing'}));

        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({
            messageType: 'localWell', 
            payload: wells,
            userEmail: user?.value?.email,
            assistedForecast: options?.assistedForecast,
            params: well?.params,
            tcParams: typeCurve?.params
          }));
        };
      };
    } catch (error) {
      console.log(error);
      return;
    };
    
  };


  function handleNewWellClick(e) {
    e.stopPropagation();
    setNewWellVisible(true);
  };

  function editListClick(e) {
    e.stopPropagation();
    setEditListVisible(true);
  };

  function newTypeCurveClick(e) {
    e.stopPropagation();
    setNewTypeCurveVisible(true);
  };

  function newGroupWellClick(e) {
    e.stopPropagation();
    setNewGroupWellVisible(true);
  };

  function handleTabChange(e, newValue) {
    e.stopPropagation();
    dispatch(setForecastListTab({listId: listId, forecastListTab: newValue}));
    if (newValue === 1 && typeCurves.allIds.length === 0) {
      setIsLoadingData(true);
      loadTypeCurves(user.value.email);
    };
    if (newValue === 2 && groupWells.allIds.length === 0) {
      setIsLoadingData(true);
      loadGroupWells(user.value.email);
    };
  };

  function newWellClose() {
    setNewWellVisible(false);
  };

  function editListClose() {
    setEditListVisible(false);
  };

  function newTypeCurveClose() {
    setNewTypeCurveVisible(false);
  };

  function newGroupWellClose() {
    setNewGroupWellVisible(false);
  };

  function editTypeCurveClose() {
    setEditTypeCurveVisible(false);
  };

  function editGroupWellClose() {
    setEditGroupWellVisible(false);
  };

  function checkWellClick(e, wellUwi) {
    e.preventDefault();
    e.stopPropagation();

    const well = wells?.byUwi?.[wellUwi];
    if (well?.checked && allWells?.currentWellUwi === wellUwi) {
      const index = wells?.allUwis?.indexOf(wellUwi);
      dispatch(makeWellCurrent({listId: listId, wellUwi: wells?.allUwis?.[index - 1] || ""}));
    } else {
      if (!allWells?.currentWellUwi) dispatch(makeWellCurrent({listId: listId, wellUwi: wellUwi }));
    };

    dispatch(toggleCheckWell({wellUwi}));

    if (isolateActiveWell) setIsolateActiveWell(false);
  };
  
  function getCheckedWellsIcon() {
    const allListUwis = [...wells?.allUwis?.filter(uwi => allUwis.indexOf(uwi) !== -1)];
    const totalWells = allListUwis?.length || 0;
    let checkedWells = 0;
    allListUwis?.forEach(uwi => {
      if (allWells?.byUwi?.[uwi]?.checked) checkedWells++;
    });
    if (checkedWells === 0) return <CheckBoxOutlineBlank/>
    if (totalWells !== checkedWells) return <IndeterminateCheckBox />
    if (totalWells === checkedWells) return <CheckBox />
  };
  
  function handleCheckAllWells() {
    const uwis = [...wells?.allUwis?.filter(uwi => allUwis.indexOf(uwi) !== -1)] || [];
    const totalWells = uwis?.length || 0;
    let checkedWells = 0;
    uwis?.forEach(uwi => {
      if (allWells?.byUwi?.[uwi]?.checked) checkedWells++;
    });
    if (checkedWells === 0 || checkedWells < totalWells) {
      dispatch(toggleCheckAllWells({wellUwis: uwis}));
    } else {
      dispatch(toggleUncheckAllWells({wellUwis: uwis}));
      dispatch(makeWellCurrent({listId: listId, wellUwi: ""}));
    };

    if (isolateActiveWell) setIsolateActiveWell(false);
  };

  function checkTypeCurveClick(e, typeCurveId) {
    e.preventDefault();
    e.stopPropagation();
    const tc = typeCurves?.byId?.[typeCurveId];
    if (tc?.checked && list?.typeCurveId === typeCurveId) {
      const checkedTypeCurves = allTypeCurveIds?.filter(id => typeCurves?.byId?.[id]?.checked);
      const index = checkedTypeCurves?.indexOf(typeCurveId);
      dispatch(makeTypeCurveCurrent({listId: listId, typeCurveId: checkedTypeCurves?.[index - 1] || ""}));
    } else {
      if (!list?.typeCurveId) dispatch(makeTypeCurveCurrent({listId: listId, typeCurveId: typeCurveId}));
    };
    dispatch(toggleCheckTypeCurve({ typeCurveId }));
  };

  function getCheckedTypeCurvesIcon() {
    // const totalTypeCurves = typeCurves?.allIds?.length || 0;
    const totalTypeCurves = allTypeCurveIds?.length || 0;
    let checkedTypeCurves = 0;
    allTypeCurveIds?.forEach(id => {
      if (typeCurves?.byId?.[id]?.checked) checkedTypeCurves++;
    });
    if (checkedTypeCurves === 0) return <CheckBoxOutlineBlank/>
    if (checkedTypeCurves !== totalTypeCurves) return <IndeterminateCheckBox />
    if (checkedTypeCurves === totalTypeCurves) return <CheckBox />
  };

  function handleCheckAllTypeCurves() {
    const ids = allTypeCurveIds || [];
    const totalTypeCurves = ids?.length || 0;
    let checkedTypeCurves = 0;
    ids?.forEach(id => {
      if (typeCurves?.byId?.[id]?.checked) checkedTypeCurves++;
    });
    if (checkedTypeCurves === 0 || checkedTypeCurves < totalTypeCurves) {
      dispatch(toggleCheckAllTypeCurves({typeCurveIds: ids}));
    } else {
      dispatch(toggleUncheckAllTypeCurves({typeCurveIds: ids}));
      dispatch(makeTypeCurveCurrent({listId: listId, groupWellId: ""}));
    };
  };

  function checkGroupWellClick(e, groupWellId) {
    e.preventDefault();
    e.stopPropagation();

    const gw = groupWells?.byId?.[groupWellId];
    if (gw?.checked && list?.groupWellId === groupWellId) {
      const checkedGroupWells = allGroupWellIds?.filter(id => groupWells?.byId?.[id]?.checked);
      const index = checkedGroupWells?.indexOf(groupWellId);
      dispatch(makeGroupWellCurrent({listId: listId, groupWellId: checkedGroupWells?.[index - 1] || ""}));
    } else {
      if (!list?.groupWellId) dispatch(makeGroupWellCurrent({listId: listId, groupWellId: groupWellId}));
    };

    dispatch(toggleCheckGroupWell({groupWellId}));
  };

  function getCheckedGroupWellsIcon() {
    const totalGroupWells = allGroupWellIds?.length || 0;
    let checkedGroupWells = 0;
    allGroupWellIds?.forEach(id => {
      if (groupWells?.byId?.[id]?.checked) checkedGroupWells++;
    });
    if (checkedGroupWells === 0) return <CheckBoxOutlineBlank/>
    if (checkedGroupWells !== totalGroupWells) return <IndeterminateCheckBox />
    if (checkedGroupWells === totalGroupWells) return <CheckBox />
  };

  function handleCheckAllGroupWells() {
    const ids = allGroupWellIds || [];
    const totalGroupWells = ids?.length || 0;
    let checkedGroupWells = 0;
    ids?.forEach(id => {
      if (groupWells?.byId?.[id]?.checked) checkedGroupWells++;
    });
    if (checkedGroupWells === 0 || checkedGroupWells < totalGroupWells) {
      dispatch(toggleCheckAllGroupWells({groupWellIds: ids}));
    } else {
      dispatch(toggleUncheckAllGroupWells({groupWellIds: ids}));
      dispatch(makeGroupWellCurrent({listId: listId, groupWellId: ""}));
    };
  };

  function makeListCurrentClick(e, listId) {
    e.preventDefault();
    e.stopPropagation();
    if (lists.currentListId !== listId) {
      dispatch(makeListCurrent(listId));
    };
  };

  function makeWellCurrentClick(e, listId, wellUwi) {
    // e.preventDefault();
    // e.stopPropagation();
    if (wellUwi !== currentUwi) {
      dispatch(makeWellCurrent({listId, wellUwi}));
      if (isolateActiveWell) {
        dispatch(toggleCheckAllWells({wellUwis: allUwis}));
        setIsolateActiveWell(false);
      };
    };
  };


  function handleDeleteWellClick(e, listId, wellUwi) {
    e.preventDefault();
    e.stopPropagation();
    dispatch(deleteWell({listId, wellUwi}));
  };

  function getWellStatus(well) {
    try {
      if (well?.status === 'processed') {
        let fluids = well.production?.fluids || [];
        return fluids.join(', ').toUpperCase();
      };
      return well.status.toUpperCase();
    } catch (error) {
      return "";
    };
  };
 
};
